import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { ConfigurationType, LevelType } from 'data/types';
import { produce } from 'immer';
import { AIR_HANDLER_GRID_SQUARES } from 'utils/air-handler-grid-squares';
import { isWallMountedAirHandler } from 'utils/air-handler-mounting';
import {
  findLevelForSpace,
  findNearestSpace,
  getContainingSpace,
  getGridSpacesForLevel,
  snapToInnerEdge
} from 'utils/grid';
import { syncAirHandlerLinesetAnchors } from 'utils/lineset-anchors';

const SNAP_EPSILON_FT = 0.25;
const ANCHOR_EPSILON_FT = 1e-6;

function snapWallMountedAirHandlersInConfig(
  config: ConfigurationType,
  structure: LevelType[]
): boolean {
  let changed = false;
  const iconPx = AIR_HANDLER_GRID_SQUARES * STRUCTURE_GRID_SIZE;

  config.condensers.forEach((condenser) => {
    condenser.zones.forEach((zone) => {
      const { airHandler } = zone;
      if (
        !airHandler ||
        airHandler.userPositioned ||
        !isWallMountedAirHandler(airHandler.airHandlerTypeCode)
      ) {
        return;
      }

      const firstItem = zone.zoneItems[0];
      if (!firstItem) return;

      const level = findLevelForSpace(firstItem.structureItemId, structure);
      if (!level) return;

      const levelIndex = structure.findIndex(
        (candidate) => candidate.uniqueId === level.uniqueId
      );
      if (levelIndex < 0) return;

      const gridSpaces = getGridSpacesForLevel(structure, levelIndex);
      const resolveSnap = (point: {
        x: number;
        y: number;
      }): { x: number; y: number } | null => {
        const space =
          getContainingSpace(point, iconPx, gridSpaces) ??
          findNearestSpace(point, iconPx, gridSpaces);
        if (!space) return null;
        return snapToInnerEdge(point, iconPx, space, {
          allGridSpaces: gridSpaces,
          structure
        });
      };

      const current = { x: airHandler.positionX, y: airHandler.positionY };
      const snapped = resolveSnap(current);
      if (!snapped) return;

      // Only accept a move to a stable position: re-snapping from the target
      // must land on the target again. Without this, a snap target that maps
      // back to a different position makes this reconciler dispatch a changed
      // configuration on every run — an infinite setConfigurations loop that
      // crashes the page (React "maximum update depth exceeded", AHA-6859).
      let target = snapped;
      if (
        Math.hypot(snapped.x - current.x, snapped.y - current.y) >=
        SNAP_EPSILON_FT
      ) {
        const resnapped = resolveSnap(snapped);
        const stable =
          !!resnapped &&
          Math.hypot(resnapped.x - snapped.x, resnapped.y - snapped.y) <
            SNAP_EPSILON_FT;
        if (!stable) {
          target = current;
        }
      }

      const positionMoved =
        Math.hypot(target.x - current.x, target.y - current.y) >=
        SNAP_EPSILON_FT;
      const centerX = target.x + AIR_HANDLER_GRID_SQUARES / 2;
      const centerY = target.y + AIR_HANDLER_GRID_SQUARES / 2;
      const lineset = config.lineSets?.find(
        (candidate) => candidate.airHandlerUniqueId === airHandler.uniqueId
      );
      const lastPoint = lineset?.points[lineset.points.length - 1];
      const endNeedsSync =
        !!lastPoint &&
        Math.hypot(
          lastPoint.positionX - centerX,
          lastPoint.positionY - centerY
        ) >= ANCHOR_EPSILON_FT;

      if (!positionMoved && !endNeedsSync) {
        return;
      }

      if (positionMoved) {
        airHandler.positionX = target.x;
        airHandler.positionY = target.y;
      }
      syncAirHandlerLinesetAnchors(
        config,
        airHandler.uniqueId,
        { x: centerX, y: centerY },
        level.uniqueId
      );
      changed = true;
    });
  });

  return changed;
}

export function snapWallMountedAirHandlersInConfigurations(
  configurations: ConfigurationType[],
  structure: LevelType[]
): ConfigurationType[] | null {
  let changed = false;
  const next = produce(configurations, (draft) => {
    draft.forEach((config) => {
      if (snapWallMountedAirHandlersInConfig(config, structure)) {
        changed = true;
      }
    });
  });
  return changed ? next : null;
}
